// In src/components/GroupChat.js

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import {
  collection, query, orderBy, onSnapshot, addDoc, serverTimestamp,
  doc, updateDoc
} from 'firebase/firestore';
import { isToday, isSameDay, format } from 'date-fns';

import { 
  Box, TextField, Button, Paper, Typography, List, ListItem, 
  ListItemText, CircularProgress, Avatar
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

// This component receives the group ID and the church ID as props
function GroupChat({ groupId, churchId }) {
  const { currentUser, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to the latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  // Fetch messages in real-time
  useEffect(() => {
    if (!groupId || !churchId) return;

    const messagesColRef = collection(db, 'churches', churchId, 'groups', groupId, 'groupChat');
    const q = query(messagesColRef, orderBy('sentAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(fetchedMessages);
    });

    return () => unsubscribe();
  }, [groupId, churchId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === '' || isSending) return;

    setIsSending(true);
    const messagesColRef = collection(db, 'churches', churchId, 'groups', groupId, 'groupChat');
    const groupDocRef = doc(db, 'churches', churchId, 'groups', groupId);

    try {
      // Add the new message to the subcollection
      await addDoc(messagesColRef, {
        text: newMessage,
        senderId: currentUser.uid,
        senderName: userProfile.firstName, // Store sender's first name
        sentAt: serverTimestamp(),
      });

      // Update the parent group document with the latest message info for notifications
      await updateDoc(groupDocRef, {
        'chatInfo.lastMessageText': newMessage,
        'chatInfo.lastMessageAt': serverTimestamp(),
      });

      setNewMessage('');
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message.");
    } finally {
      setIsSending(false);
    }
  };
  
  // Helper function to format the timestamp for display
  const formatTimestamp = (timestamp) => {
    // This check is important for when the component first renders
    if (!timestamp) return ''; 
    const date = timestamp.toDate();
    if (isToday(date)) {
      return format(date, 'p'); // e.g., "12:30 PM"
    }
    return format(date, 'MMM d, p'); // e.g., "Jul 1, 12:30 PM"
  };

  return (
    // This Box is styled to act like a mobile chat app screen
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 280px)', // Adjust height to fit within the group detail page
        maxHeight: '600px',
        bgcolor: 'grey.50',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* Message List Area */}
      <Paper
        elevation={0}
        sx={{ flexGrow: 1, overflow: 'auto', p: 2, bgcolor: 'transparent' }}
      >
        <List>
          {messages.map((msg, index) => {
            const isUserMessage = msg.senderId === currentUser.uid;

            // CORRECTED: Safely handle potentially null timestamps
            const currentMsgDate = msg.sentAt ? msg.sentAt.toDate() : null;
            const prevMsgDate = messages[index - 1]?.sentAt ? messages[index - 1].sentAt.toDate() : null;
            const showDateHeader = currentMsgDate && (!prevMsgDate || !isSameDay(prevMsgDate, currentMsgDate));
            
            return (
              <React.Fragment key={msg.id}>
                {showDateHeader && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', my: 1 }}>
                    {format(currentMsgDate, 'MMMM d, yyyy')}
                  </Typography>
                )}
                <ListItem
                  sx={{
                    display: 'flex',
                    flexDirection: isUserMessage ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    mb: 1,
                    px: 0,
                  }}
                >
                  <Avatar sx={{ bgcolor: isUserMessage ? 'primary.main' : 'secondary.main', mx: 1 }}>
                    {msg.senderName?.charAt(0)}
                  </Avatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isUserMessage ? 'flex-end' : 'flex-start' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                      {msg.senderName}
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: isUserMessage ? 'primary.light' : 'grey.200',
                        p: '8px 12px',
                        borderRadius: '18px',
                        borderTopLeftRadius: isUserMessage ? '18px' : '4px',
                        borderTopRightRadius: isUserMessage ? '4px' : '18px',
                        maxWidth: '100%',
                      }}
                    >
                      <ListItemText 
                        primary={msg.text} 
                        primaryTypographyProps={{ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }} 
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {/* CORRECTED: Show "Sending..." if timestamp is null */}
                      {msg.sentAt ? formatTimestamp(msg.sentAt) : 'Sending...'}
                    </Typography>
                  </Box>
                </ListItem>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </List>
      </Paper>
      
      {/* Message Input Form */}
      <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          autoComplete="off"
          size="small"
        />
        <Button type="submit" variant="contained" disabled={isSending} sx={{ ml: 1 }}>
          {isSending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
        </Button>
      </Box>
    </Box>
  );
}

export default GroupChat;